/**
 * How the job list asks the backend which jobs to return.
 *
 * `GET /jobs` accepts `projectId` and `teamId` but rejects a request carrying
 * both with a 400. Modelling the choice as a discriminated union rather than two
 * optional parameters makes that combination unrepresentable, so the rule is
 * enforced by the compiler instead of discovered at runtime.
 *
 * The three modes are backend behaviours, not client-side filters:
 * - `project`: the single project, after a ProjectAction.View check.
 * - `team`: the union of projects in that team the caller can view.
 * - `global`: every project the caller is a member of, or - for a deployment
 *   admin - everything.
 */
export type JobDiscoveryScope = { mode: "project"; projectId: string } | { mode: "team"; teamId: string } | { mode: "global" };

export const projectScope = (projectId: string): JobDiscoveryScope => ({ mode: "project", projectId });
export const teamScope = (teamId: string): JobDiscoveryScope => ({ mode: "team", teamId });
export const globalScope = (): JobDiscoveryScope => ({ mode: "global" });

/**
Stable identity for a scope, for use as an effect dependency or cache key.
*/
export const toScopeKey = (scope: JobDiscoveryScope): string => {
  switch (scope.mode) {
    case "project": {
      return `project:${scope.projectId}`;
    }
    case "team": {
      return `team:${scope.teamId}`;
    }
    default: {
      return "global";
    }
  }
};

/**
 * Resolves the scope implied by the active context, falling back outward:
 * project, then team, then global. A caller that wants a specific mode should
 * construct it directly rather than going through this.
 */
export const resolveDefaultScope = (activeTeamId: string, activeProjectId: string): JobDiscoveryScope => {
  if (activeProjectId) return projectScope(activeProjectId);
  return activeTeamId ? teamScope(activeTeamId) : globalScope();
};

/**
 * Which disambiguating columns a job row needs for a given scope (WP7.1): a row's project once
 * the scope is wider than a single project, and its team only once the scope is wider than a
 * single team (global). `shouldForceProjectColumn`/`shouldForceTeamColumn` let a page require a
 * column regardless of scope (e.g. the My Projects aggregate, which is always effectively global).
 */
export const getDiscoveryColumnVisibility = (scope: JobDiscoveryScope, shouldForceProjectColumn = false, shouldForceTeamColumn = false): { showProjectColumn: boolean; showTeamColumn: boolean } => ({
  showProjectColumn: shouldForceProjectColumn || scope.mode !== "project",
  showTeamColumn: shouldForceTeamColumn || scope.mode === "global",
});
